import { BadgePercent, Calendar, Ellipsis, Menu } from 'lucide-react';
import * as moment from 'moment';
import * as React from 'react';
import * as ReactDates from 'react-dates';
import Add from '../../components_v2/add/Add';
import { Table, TableSortType } from '../../components_v2/table/Table';
import { RangePickerModal } from '../../components_v2/toolbarFilter/time/DatePicker';
import { Translate, translateToString } from '../../styles/global/translate';
import { ToolbarState } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { DurationPickerModalWrapper } from '../import/Table';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import PageSwitch from '../map/PageSwitch';
import { ModalState } from '../products/model';
import { PaginationResult } from '../../components_v2/pagination/model/Model';
import Pagination from '../../components_v2/pagination/Pagination';
import { deletePromotionsById, getPromotions, postPromotion, putPromotion, PutPromotionBody, putPromotionProductFields } from './actions';
import GanttChart from './GanttChart';
import { Period, Promotion, PromotionProduct } from './model';
import PopupPromotion from './PopupPromotion';
import genPromotionColumns, { comparePromotionRows, PromotionRow } from './PromotionsColumns';
import { usePromotionFieldDefinitions } from './usePromotionFieldDefinitions';

import equal from 'deep-equal';
import { useRecoilState, useRecoilValue } from 'recoil';
import { AVisibleAdditionalColumns } from '../../atoms/additionalColumns';
import { AVisibleAdditionalFieldColumns, fieldTypeToFilterType } from '../../atoms/additionalFieldColumns';
import { AVisibleCalculatedFields, APromotionCalculatedFields, CalculatedFieldTarget } from '../../atoms/calculatedFields';
import { AFormFields, Field, getFields } from '../../atoms/forms';
import { AViews, DbView, deleteView, getViews, NewView, postView, PromotionsView, putView, ViewCompanyColumns } from '../../atoms/global/views';
import Switch from '../../components_v2/Switch/Switch';
import { SwitchText } from '../calendar/style/NewEventStyle';
import { Column as OrganizerColumn, ColumnOrganizerWrapper } from '../client-companies/popup/ColumnOrganizer';
import { AProducts } from '../../atoms/product';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import advancedFiltersInterpretor from '../../components_v2/filter/AdvancedFiltersInterpretor';
import { FilterId, FilterResult, FilterTree, filterTreeLength, PromotionFilter, unwrapFilterId } from '../../components_v2/filter/model/Model';
import { convertFilters, Filter, FilterParameter } from '../../components_v2/filter/pages/FilterList';
import { getCompanies } from '../client-companies/data/action';
import { getProductFieldValues } from './actions';
import { getProductDimensionValues } from '../products/action';
import {
	collectLinkedCompanyLeaves, companyFilterList, compareLeafValue, LINKED_COMPANY, LINKED_PRODUCT,
	collectProductDimFieldIds, linkedCompanyLeafKey, LinkedFilterContext, linkedMatch, productFilterList, wrapFilterParams,
} from './perimeterFilters';
import { PopupMode } from '../../components_v2/popup/model/Model';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import PopupView, { ViewCreation } from '../../components_v2/view/PopupView';
import ViewPicker from '../../components_v2/view/ViewPicker';
import { GreySidely, GreySidely3, SidelyBlack } from '../../styles/global/css/Utils';
import { useFunctionState } from '../../utils/customHooks';
import useAlert from '../alert/UseAlert';
import { SidebarButton } from '../client-companies/style/Style';
import { ToolbarBox, ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { Unhautorized } from '../noData/NoData';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { FlexDiv } from '../products/style';
import { defaultFilters } from '../reports/interpretor/ReportInterpretor';
import exportPromotions from './export';
import importPromotions from './import';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

// a table line's selection key: the promotion, or the (promotion, product)
// pair when the one-line-per-product toggle is on
function lineKey(row: PromotionRow): string {
	return `${row.id}:${row.product?.id ?? ''}`;
}

export type RangePickerState = {
	isOpen: boolean,
	left?: number,
	top?: number,
	isTop?: boolean,
	dates?: { startDate: moment.Moment | null, endDate: moment.Moment | null }
	onDatesChange?: (p: { startDate: moment.Moment | null, endDate: moment.Moment | null }) => void,
	focus?: ReactDates.FocusedInputShape
}

// everyPeriod: negative operators (≠, ne contient pas, est vide…) must hold
// on EVERY period — "some period differs" would match a promotion that still
// carries the value on another period, and a period without the field
// satisfies a negative
function generalPromoFilter(promotion: Promotion, filterId: PromotionFilter, compareFunction: (fieldValue: any) => boolean, everyPeriod?: boolean): boolean {
	const promotionFilter = filterId.promotions;
	if (!promotion || !promotionFilter) return false;
	const normalize = (value: unknown): unknown => {
		// the editors clear a value to null (text, number, select), to '' (the
		// boolean switch) or to [] (a multiselect emptied of every choice).
		// None of those is a value, so all of them have to read as absent —
		// "est vide" used to test undefined alone, so it never matched a field
		// that had been filled once and "n'est pas vide" matched it
		if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return undefined;
		// temporal values (period bounds, date custom fields) compare as
		// timestamps on BOTH sides, matching parseCompareValue's parsing — a
		// raw ISO string against a parsed number would never match
		if (promotionFilter.filter_type === 'temporal') {
			const ms = typeof value === 'string' ? Date.parse(value) : NaN;
			return isNaN(ms) ? undefined : ms;
		}
		// a Boolean field holds a real boolean while the widget sends its
		// IS_TRUE/IS_FALSE as eq 'true'/'false': compare as text, the way the
		// period_active branch below already does
		if (promotionFilter.filter_type === 'bool') return String(value);
		// text compares case-insensitively, like the promotion name filter and
		// like the server-side company filters
		if (promotionFilter.filter_type === 'string') return String(value).toLowerCase();
		return value;
	};
	// the period date bounds: field_id carries the period index, so which
	// period is tested is explicit in the filter itself
	if (promotionFilter.type === 'period_start' || promotionFilter.type === 'period_end') {
		const period = promotion.periods?.[promotionFilter.field_id];
		return compareFunction(normalize(period?.[promotionFilter.type === 'period_start' ? 'start' : 'end']));
	}
	// active campaign: at least one period contains today (a period without
	// an end is open-ended, like the companies filter). The bool filter's
	// IS_TRUE/IS_FALSE arrive as eq 'true'/'false', so compare as strings
	if (promotionFilter.type === 'period_active') {
		const now = Date.now();
		const active = promotion.periods?.some(p => p.start && Date.parse(p.start) <= now && (!p.end || now <= Date.parse(p.end))) ?? false;
		return compareFunction(String(active));
	}
	if (promotionFilter.type === 'promotion') {
		return compareFunction(normalize(promotion.fields?.[promotionFilter.field_id]));
	}
	if (promotionFilter.type === 'period') {
		const periods = promotion.periods ?? [];
		const test = (period: Period) => compareFunction(normalize(period?.fields?.[promotionFilter.field_id]));
		return everyPeriod ? periods.every(test) : periods.some(test);
	}
	// a field ticked both promotion and product: one value per (promotion,
	// product) link, so the test quantifies over the linked products the way
	// the period branch quantifies over periods — a negative must hold on
	// every product, and a promotion with no product satisfies it
	if (promotionFilter.type === 'product') {
		const products = promotion.products ?? [];
		const test = (product: PromotionProduct) => compareFunction(normalize(product.fields?.[promotionFilter.field_id]));
		return everyPeriod ? products.every(test) : products.some(test);
	}
	return false;
}

// A Multiselect value is an array: a comparison tests its elements one by one
// and one match is enough. Comparing the array itself against a choice is what
// made every multi-choice custom-field filter match nothing
function someValue(fieldValue: unknown, test: (value: unknown) => boolean): boolean {
	return Array.isArray(fieldValue) ? fieldValue.some(test) : test(fieldValue);
}

function parseCompareValue(compare: string | undefined, filterId: PromotionFilter, split?: boolean): any {
	if (!compare) return undefined;
	switch (filterId.promotions.filter_type) {
		case 'string':
			// normalize() lowercases the stored side to match
			return compare.toLowerCase();
		case 'numeric':
			return split ? compare.split(',').map((f) => parseFloat(f)) : parseFloat(compare);
		// a Money field is stored in cents (fieldEditor's parseValue), so the
		// typed amount has to travel the same way — 15 against a stored 1500
		// never matched anything
		case 'money':
			return split ? compare.split(',').map((f) => Math.round(parseFloat(f) * 100)) : Math.round(parseFloat(compare) * 100);
		case 'select':
		case 'multi_select':
			return split ? compare.split(',') : compare;
		// dates arrive as ISO strings (no comma inside), compared as timestamps
		case 'temporal':
			return split ? compare.split(',').map((d) => Date.parse(d)) : Date.parse(compare);
		default:
			return compare;
	}
}

function advancedFilterPromotions(promotion: Promotion, filterResult: FilterResult, ctx: LinkedFilterContext): boolean {
	// every operator first checks for a linked filter; combinator/tag exist
	// only for linked company filters (company tags), so they default to false
	const options = Object.fromEntries(
		Object.entries({ ...basePromotionOptions, tag: undefined, combinator: undefined, not_combinator: undefined })
			.map(([op, fn]) => [op, (value: Promotion, a?: string | FilterId, b?: FilterId) => {
				const noValue = op === 'empty' || op === 'not_empty';
				const filterId = (noValue ? a : b) as FilterId;
				const compare = noValue ? undefined : a as string | undefined;
				const linked = linkedMatch(value, filterId, op, compare, ctx);
				if (linked !== undefined) return linked;
				// 'name' is the built-in promotion column: compareLeafValue
				// matches the promotion's own name, case-insensitively
				if (filterId === 'name') return compareLeafValue(value.name, op, compare);
				return (fn as ((...args: unknown[]) => boolean) | undefined)?.(value, a, b) ?? false;
			}])
	);
	return advancedFiltersInterpretor(promotion, filterResult.formatted, options);
}

const basePromotionOptions = {
	empty: (value, filterId: PromotionFilter) => {
		return generalPromoFilter(value, filterId, (fieldValue) => fieldValue === undefined, true);
	},
	not_empty: (value, filterId: PromotionFilter) => {
		return generalPromoFilter(value, filterId, (fieldValue) => fieldValue !== undefined);
	},
	eq: (value, compare, filterId: PromotionFilter) => {
		return generalPromoFilter(value, filterId, (fieldValue) => someValue(fieldValue, v => v === parseCompareValue(compare, filterId)));
	},
	// negative operators quantify over every period, and an absent value
	// satisfies them (like NULLs on the companies filters)
	ne: (value, compare, filterId: PromotionFilter) => {
		return generalPromoFilter(value, filterId, (fieldValue) => !someValue(fieldValue, v => v === parseCompareValue(compare, filterId)), true);
	},
	gt: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		const compareValue = parseCompareValue(compare, filterId);
		if (compareValue === undefined) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			return fieldValue > compareValue;
		});
	},
	gte: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		const compareValue = parseCompareValue(compare, filterId);
		if (compareValue === undefined) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			return fieldValue >= compareValue;
		});
	},
	lt: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		const compareValue = parseCompareValue(compare, filterId);
		if (compareValue === undefined) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			return fieldValue < compareValue;
		});
	},
	lte: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		const compareValue = parseCompareValue(compare, filterId);
		if (compareValue === undefined) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			return fieldValue <= compareValue;
		});
	},
	contains: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			return someValue(fieldValue, v => String(v).toLowerCase().includes(compare?.toString().toLowerCase() ?? ''));
		});
	},
	not_contains: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return true;
			return !someValue(fieldValue, v => String(v).toLowerCase().includes(compare?.toString().toLowerCase() ?? ''));
		}, true);
	},
	contained: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			const compareValue = parseCompareValue(compare, filterId, true);
			if (!compareValue) return false;
			return someValue(fieldValue, v => compareValue.includes(v));
		});
	},
	not_contained: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return true;
			const compareValue = parseCompareValue(compare, filterId, true);
			if (!compareValue) return false;
			return !someValue(fieldValue, v => compareValue.includes(v));
		}, true);
	},
	between: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			const compareValue = parseCompareValue(compare, filterId, true);
			if (!compareValue || compareValue.length < 2) return false;
			return compareValue[0] <= fieldValue && fieldValue <= compareValue[1];
		});
	},
	not_between: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return true;
			const compareValue = parseCompareValue(compare, filterId, true);
			if (!compareValue || compareValue.length < 2) return false;
			return !(compareValue[0] <= fieldValue && fieldValue <= compareValue[1]);
		}, true);
	},
	starts_with: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			if (typeof compare !== 'string') return false;
			return someValue(fieldValue, v => String(v).toLowerCase().startsWith(compare.toLowerCase()));
		});
	},
	ends_with: (value, compare, filterId: PromotionFilter) => {
		if (!compare) return false;
		return generalPromoFilter(value, filterId, (fieldValue) => {
			if (fieldValue === undefined) return false;
			if (typeof compare !== 'string') return false;
			return someValue(fieldValue, v => String(v).toLowerCase().endsWith(compare.toLowerCase()));
		});
	}
};

export default function Promotions(props: { setToolBarState: React.Dispatch<React.SetStateAction<ToolbarState>> }) {
	const [promotions, setPromotions] = React.useState<Promotion[]>([]);
	const [rangePickerState, setRangePickerState] = React.useState<RangePickerState>({ isOpen: false });
	const [modalState, setModalState] = React.useState<ModalState<{ promotion: Promotion, mode: PopupMode }>>({ isOpen: false });
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();
	// table by default; the last choice sticks, like the promotions_view_id preset
	const [visu, rawSetVisu] = React.useState<'table' | 'timeline'>(() =>
		localStorage.getItem('promotions_visu') === 'timeline' ? 'timeline' : 'table');
	const setVisu = React.useCallback((value: 'table' | 'timeline') => {
		localStorage.setItem('promotions_visu', value);
		rawSetVisu(value);
	}, []);
	const [archivedFilter, setArchivedFilter] = React.useState(true);
	const [sort, setSort] = React.useState<TableSortType>({ id: 'created_at', desc: true });
	// one line per linked product + column management, persisted like visu and
	// carried by views
	const [inlineProducts, rawSetInlineProducts] = React.useState<boolean>(() => localStorage.getItem('promotions_inline_products') === 'true');
	const setInlineProducts = React.useCallback((value: boolean) => {
		localStorage.setItem('promotions_inline_products', String(value));
		rawSetInlineProducts(value);
	}, []);
	const [hiddenColumns, rawSetHiddenColumns] = React.useState<string[]>(() => {
		try { return JSON.parse(localStorage.getItem('promotions_hidden_columns') ?? '[]'); } catch { return []; }
	});
	const setHiddenColumns = React.useCallback((value: string[]) => {
		// the pinned identity columns can never be hidden, whatever an old
		// view carries
		const cleaned = value.filter(id => id !== 'name' && id !== 'product_name');
		localStorage.setItem('promotions_hidden_columns', JSON.stringify(cleaned));
		rawSetHiddenColumns(cleaned);
	}, []);
	const [tableResize, setTableResize] = React.useState<Record<string, number>>(() => {
		try { return JSON.parse(localStorage.getItem('table_promotions_resize') ?? '{}'); } catch { return {}; }
	});
	React.useEffect(() => {
		localStorage.setItem('table_promotions_resize', JSON.stringify(tableResize));
	}, [tableResize]);
	const [columnOrder, rawSetColumnOrder] = React.useState<string[]>(() => {
		try { return JSON.parse(localStorage.getItem('promotions_column_order') ?? '[]'); } catch { return []; }
	});
	const setColumnOrder = React.useCallback((value: string[]) => {
		localStorage.setItem('promotions_column_order', JSON.stringify(value));
		rawSetColumnOrder(value);
	}, []);
	const products = useRecoilValue(AProducts);
	const fields = usePromotionFieldDefinitions();
	const alert = useAlert();
	const [isBulkDeleteOpen, setIsBulkDeleteOpen] = React.useState(false);
	// checked lines; the meaning follows the display mode: promotions, or
	// (promotion, product) pairs when inlining — see lineKey
	const [selectedLines, setSelectedLines] = React.useState<Set<string>>(new Set());
	const [isSelectionDeleteOpen, setIsSelectionDeleteOpen] = React.useState(false);

	//Filters
	const [filterResult, setFilterResult] = React.useState<FilterResult>(defaultFilters);
	const [filterOpen, setFilterOpen] = React.useState(false);
	// the column-header filter buttons open the panel on that very filter
	const [selectedFilter, setSelectedFilter] = React.useState<FilterId>();
	const [isOpenSummary, setIsOpenSummary] = React.useState(false);
	const [promotionFields, setPromotionFields] = React.useState<Field[]>();
	// the link-level fields, one column per field when inlining products
	const linkFields = React.useMemo(() => fields.filter(f => f.is_promotion && f.is_product), [fields]);
	const promotionFilters: Filter[] = React.useMemo(() => {
		// the name is a built-in promotion attribute: it filters even when no
		// custom field is attached to promotions
		const nameFilter: Filter = { id: 'name', name: translateToString('name'), type: 'string' };
		return [nameFilter, ...(promotionFields ?? []).map((field): Filter => ({
			name: `${field.name} (${translateToString('campaigns.campaign')})`,
			id: { promotions: { type: 'promotion', field_id: field.id, filter_type: fieldTypeToFilterType(field.type) } },
			type: fieldTypeToFilterType(field.type),
		})),
		// a field ticked both promotion and product holds one value per
		// (promotion, product) link, so it was absent from this list: the
		// columns showed those values with no way to filter on them
		...linkFields.map((field): Filter => ({
			name: `${field.name} (${translateToString('campaigns.product')})`,
			id: { promotions: { type: 'product', field_id: field.id, filter_type: fieldTypeToFilterType(field.type) } },
			type: fieldTypeToFilterType(field.type),
		}))];
	}, [promotionFields, linkFields]);
	// the period level filters on its date bounds, one pair per period index
	// (periods are dynamic: as many entries as the longest loaded promotion,
	// like the listing columns). field_id carries the period index. The old
	// custom-field variants were dropped: the web never writes period.fields,
	// so they matched nothing and only duplicated the campaign fields
	const periodFilters: Filter[] = React.useMemo(() => {
		const count = promotions.reduce((acc, p) => Math.max(acc, p.periods.length), 1);
		return [
			// active campaigns: at least one period contains today
			{
				name: translateToString('current_campagnes'),
				id: { promotions: { type: 'period_active', field_id: 0, filter_type: 'bool' } },
				type: 'bool',
			} as Filter,
		].concat(Array.from({ length: count }).flatMap((_, i): Filter[] => [
			{
				name: `${translateToString('import.sidely_fields.start_date')} (${translateToString('period')} ${i + 1})`,
				id: { promotions: { type: 'period_start', field_id: i, filter_type: 'temporal' } },
				type: 'temporal',
			},
			{
				name: `${translateToString('import.sidely_fields.end_date')} (${translateToString('period')} ${i + 1})`,
				id: { promotions: { type: 'period_end', field_id: i, filter_type: 'temporal' } },
				type: 'temporal',
			},
		]));
	}, [promotions]);

	// linked-company filters: the very list the companies page offers, each id
	// wrapped so evaluation resolves through /client-companies/query
	const additionalColumns = useRecoilValue(AVisibleAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AVisibleAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(AVisibleCalculatedFields);
	const [companyFilterParams, setCompanyFilterParams] = React.useState<FilterParameter[]>([]);
	React.useEffect(() => {
		companyFilterList(additionalColumns, additionalFieldColumns, calculatedFields)
			.then(params => setCompanyFilterParams(wrapFilterParams(params, LINKED_COMPANY, translateToString('companies'))))
			.catch(console.error);
	}, [additionalColumns, additionalFieldColumns, calculatedFields]);

	const allFields = useRecoilValue(AFormFields);

	// each linked-company leaf resolves to the set of matching company ids
	const [linkedCompanySets, setLinkedCompanySets] = React.useState<Record<string, Set<number>>>({});
	React.useEffect(() => {
		const leaves = collectLinkedCompanyLeaves(filterResult?.formatted);
		if (leaves.length === 0) {
			setLinkedCompanySets({});
			return;
		}
		let cancelled = false;
		Promise.all(leaves.map(async leaf => {
			const res = await getCompanies({ new_filters: { val: { ...leaf, column: unwrapFilterId(leaf.column) } } as FilterTree, columns: [], order_bys: undefined }, true);
			return [linkedCompanyLeafKey(leaf.column, leaf.operator, leaf.value), new Set(res.companies.map(c => c.company_id))] as const;
		})).then(entries => {
			if (!cancelled) setLinkedCompanySets(Object.fromEntries(entries));
		}).catch(console.error);
		return () => {
			cancelled = true;
		};
	}, [filterResult]);

	const productsById = React.useMemo(() => new Map(products.map(p => [p.id, p])), [products]);
	const [productFieldValues, setProductFieldValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	const [dimensionValues, setDimensionValues] = React.useState<Map<number, Map<string, unknown>>>(new Map());
	// only the dimension fields the active filters reference are fetched
	React.useEffect(() => {
		const missing = [...collectProductDimFieldIds(filterResult?.formatted)].filter(id => !dimensionValues.has(id));
		if (missing.length === 0) return;
		getProductDimensionValues(missing)
			.then(fetched => setDimensionValues(prev => new Map([...prev, ...fetched])))
			.catch(console.error);
	}, [filterResult, dimensionValues]);
	React.useEffect(() => {
		getProductFieldValues().then(rows => {
			const map = new Map<number, Map<string, unknown>>();
			rows.forEach(r => {
				if (!map.has(r.field_id)) map.set(r.field_id, new Map());
				map.get(r.field_id)?.set(r.product_uuid, r.value);
			});
			setProductFieldValues(map);
		}).catch(console.error);
	}, []);
	// linked-product filters: hardcoded product attributes + product custom
	// fields, the form-collected entries gated on fields that have values
	const productFilters: Filter[] = React.useMemo(() =>
		wrapFilterParams([{ category: '', filters: productFilterList(allFields, new Set(productFieldValues.keys())) }], LINKED_PRODUCT)[0].filters,
	[allFields, productFieldValues]);
	const linkedFilterContext: LinkedFilterContext = React.useMemo(() => ({
		companySets: linkedCompanySets,
		productsById,
		productFieldValues,
		dimensionValues,
	}), [linkedCompanySets, productsById, productFieldValues, dimensionValues]);

	//Views
	const [deletionModalState, setDeletionModalState] = React.useState<ModalState<DbView<PromotionsView>>>({ isOpen: false });
	const [editedView, setEditedView] = React.useState<DbView<PromotionsView> | undefined>();
	const [views, setViews] = useRecoilState(AViews);
	const [isViewOpen, setIsViewOpen] = React.useState<boolean>(false);
	const [selectedView, setSelectedView] = useFunctionState<DbView<PromotionsView> | undefined>(views.promotions?.find(v => v.id.toString() === localStorage.getItem('promotions_view_id')),
		({ newValue }) => {
			if (newValue) {
				localStorage.setItem('promotions_view_id', newValue.id.toString());
			} else {
				localStorage.removeItem('promotions_view_id');
			}
			return newValue;
		}
	);
	const { isAllowedTo } = React.useContext(PermissionContext);


	React.useEffect(() => {
		// both-ticked (promotion + product) fields are link-level: their values
		// live per (promotion, product), not in promotions.fields, so they
		// filter through the 'product' leaf and not this list
		getFields().then(fields => setPromotionFields(fields.filter((field) => field.is_promotion && !field.is_product)));
	}, []);

	// everything a view carries: filters, columns (order + hidden), the
	// one-line-per-product toggle and the sort — like the companies views
	const applyView = React.useCallback((view: DbView<PromotionsView> | undefined) => {
		// views can be old (saved by previous versions of this page) or public
		// ones saved by someone else: apply what their data allows and never
		// let a malformed one break the click
		let filters = defaultFilters;
		if (view?.data.filters) {
			try {
				filters = { ...view.data.filters, formatted: view.data.filters.values ? convertFilters(view.data.filters.values) : view.data.filters.formatted };
			} catch (e) {
				console.error('unreadable view filters', view.id, e);
				filters = view.data.filters;
			}
		}
		setFilterResult(filters);
		if (view && Array.isArray(view.data.columns)) {
			setColumnOrder(view.data.columns.map(c => c.column));
			setHiddenColumns(view.data.columns.filter(c => c.hidden).map(c => c.column));
		}
		if (view?.data.inlineProducts !== undefined) setInlineProducts(view.data.inlineProducts);
		if (view?.data.sort?.id) setSort(view.data.sort);
	}, []);

	React.useEffect(() => {
		const view = views.promotions?.find(v => v.id.toString() === localStorage.getItem('promotions_view_id'));
		setSelectedView(view);
		if (view) applyView(view);
	}, [views]);

	const saveViewRef = React.useRef<{ columns: ViewCompanyColumns[], inlineProducts: boolean, sort: TableSortType }>({ columns: [], inlineProducts: false, sort });

	const saveView = React.useCallback(async(v: ViewCreation) => {
		const data: NewView<'promotions'> = {
			data: {
				filters: filterResult ?? defaultFilters,
				name: v.name,
				type: 'promotions',
				...saveViewRef.current,
			},
			public: v.public
		};
		await (v.id ? putView(v.id, data) : postView(data));
		getViews().then(newViews => {
			setSelectedView(newViews.promotions.find(nv => v.id === nv.id || !views.promotions.find(v => v.id == nv.id)));
			setViews(newViews);
		});
	}, [views, filterResult]);

	const deleteLocalView = (view: DbView<PromotionsView>) => {
		if (view) {
			setDeletionModalState({ isOpen: true, data: view });
		}
	};

	// sorting is client-side (the listing is fully loaded and the field /
	// product columns have no server order), so the fetch never re-runs on sort
	const refresh = React.useCallback(() => {
		setLoadingState(LoadingStateEnum.LOADING);
		getPromotions().then(res => {
			setPromotions(res);
			setLoadingState(LoadingStateEnum.LOADED);
		}).catch(e => {
			console.error(e);
			setLoadingState(LoadingStateEnum.ERROR);
		});
	}, []);

	const dropdownData = [{ label: translateToString('all'), value: false }, { label: translateToString('visible'), value: true }];

	const filteredPromotions = React.useMemo(() =>
		promotions.filter(p => advancedFilterPromotions(p, filterResult, linkedFilterContext)),
	[promotions, filterResult, linkedFilterContext]);

	const localPutPromotion = React.useCallback((id: number, body: PutPromotionBody) => putPromotion(id, body).then(() => refresh()), []);
	const putProductField = React.useCallback(async(promotionId: number, productId: number, fieldId: number, value: unknown) => {
		await putPromotionProductFields(promotionId, productId, { field_id: fieldId, value });
		refresh();
	}, []);

	const canUpdate = isAllowedTo({ objectAction: 'UpdateCampaign' });
	const promotionCalculatedFields = useRecoilValue(APromotionCalculatedFields);
	const columns = React.useMemo(() => genPromotionColumns({
		promotions,
		setRangePickerState,
		putPromotion: localPutPromotion,
		putProductField,
		setModalState,
		loadingState,
		canUpdate,
		promotionFields: promotionFields ?? [],
		linkFields,
		inlineProducts,
		calculatedFields: promotionCalculatedFields.filter(cf => cf.target === CalculatedFieldTarget.Promotion),
		productCalculatedFields: promotionCalculatedFields.filter(cf => cf.target === CalculatedFieldTarget.PromotionProduct),
	}), [promotions, loadingState, canUpdate, promotionFields, linkFields, inlineProducts, promotionCalculatedFields]);

	// the table rows: one per promotion, or one per (promotion, product) when
	// inlining; sorted client-side by the picked column
	const rows: PromotionRow[] = React.useMemo(() => {
		const base: PromotionRow[] = inlineProducts
			? filteredPromotions.flatMap(p => p.products.length > 0 ? p.products.map(product => ({ ...p, product })) : [p])
			: filteredPromotions;
		if (!sort) return base;
		const fieldsById = new Map([...(promotionFields ?? []), ...linkFields].map(f => [f.id, f]));
		const direction = sort.desc ? -1 : 1;
		return [...base].sort((a, b) => comparePromotionRows(a, b, sort.id, fieldsById) * direction);
	}, [filteredPromotions, inlineProducts, sort, promotionFields, linkFields]);

	// the selection changes meaning with the mode and scope with the filters:
	// reset it rather than carry ambiguous checks over
	React.useEffect(() => {
		setSelectedLines(new Set());
	}, [inlineProducts, filterResult]);

	// client-side pagination over the computed rows: the whole listing is
	// already loaded, slicing bounds the rendered DOM. The Pagination footer
	// resets to page 1 whenever the row count changes (filters, toggle)
	const [pagination, setPagination] = React.useState<PaginationResult>({ currentPage: 1, step: 25, offset: 0 });
	const pageRows = React.useMemo(() => rows.slice(pagination.offset, pagination.offset + pagination.step), [rows, pagination]);

	// inlined lines without a product carry nothing to remove: not selectable.
	// Checks accumulate across pages; the header checkbox works on the page
	const selectableRows = React.useMemo(() => inlineProducts ? rows.filter(r => r.product) : rows, [rows, inlineProducts]);
	const pageSelectableRows = React.useMemo(() => inlineProducts ? pageRows.filter(r => r.product) : pageRows, [pageRows, inlineProducts]);
	const selectedRows = React.useMemo(() => selectableRows.filter(r => selectedLines.has(lineKey(r))), [selectableRows, selectedLines]);
	const selectedPromotionIds = React.useMemo(() => [...new Set(selectedRows.map(r => r.id))], [selectedRows]);

	// the Table's selection column memoizes its Header/Cell with the callbacks
	// of the first render (react-table plugin hooks): route the live state
	// through a ref, like the contacts listing, or select-all runs against the
	// empty pre-load page
	const selectionRef = React.useRef({ selectedLines, pageSelectableRows, inlineProducts });
	selectionRef.current = { selectedLines, pageSelectableRows, inlineProducts };

	// the displayed column order: the saved order first, new columns appended.
	// The name column is pinned first and never reordered or hidden — it is
	// the row's identity and the popup's entry point
	const orderedColumnIds = React.useMemo(() => {
		// name is pinned first, and the product column right after it when the
		// one-line-per-product toggle is on: they identify the line
		const pinned = ['name', 'product_name'];
		const ids = columns.map(c => c.id ?? '');
		const known = columnOrder.filter(id => ids.includes(id) && !pinned.includes(id));
		return [...pinned.filter(id => ids.includes(id)), ...known, ...ids.filter(id => !pinned.includes(id) && !known.includes(id))];
	}, [columns, columnOrder]);

	React.useEffect(() => {
		saveViewRef.current = {
			columns: orderedColumnIds.map(id => ({ column: id, hidden: hiddenColumns.includes(id) })),
			inlineProducts,
			sort,
		};
	}, [orderedColumnIds, hiddenColumns, inlineProducts, sort]);

	const organizerColumns: OrganizerColumn[] = React.useMemo(() => columns.filter(c => c.id !== 'name' && c.id !== 'product_name').map((c): OrganizerColumn => {
		const id = c.id ?? '';
		return {
			label: typeof c.Header === 'string' ? c.Header : id,
			value: id,
			checked: !hiddenColumns.includes(id),
			sequence: orderedColumnIds.indexOf(id),
			categoryIndex: id.startsWith('link_field_') || id === 'product_name' ? 2 : id.startsWith('field_') ? 1 : 0,
		};
	}), [columns, hiddenColumns, orderedColumnIds]);

	React.useEffect(() => {
		const dataList = [
			{ label: translateToString('import.import_data') + ' (Excel / CSV)', value: 'import' },
			{ label: translateToString('export_NAME', [['NAME', translateToString('campagnes')]]), value: 'export' }
		];
		if (isAllowedTo({ objectAction: 'DeleteCampaign' })) {
			dataList.push({ label: translateToString('delete'), value: 'deleteAll' });
		}
		props.setToolBarState({
			title: translateToString('campagnes'),
			bottomLeftToolbarComponent: <ToolbarBox padding='0 0.6em'>
				<Restricted to={{ objectAction: 'ReadCampaign' }}>
					<FlexDiv gap='8px'>
						{visu === 'timeline' && <>
							<Dropdown
								datalist={dropdownData}
								dropdownStyle={{
									width: '205px',
									optionWidth: '205px',
									fontSize: 12,
									containerBorder: `1px solid color-mix(in oklab, ${SidelyBlack} 12.5%, transparent)`,
									arrowClassName: 'fas fa-caret-down mr-1',
									arrowColor: GreySidely,
									arrowFontSize: '12px',
									imageWidth: '20px',
									imageHeight: '20px'
								}}
								readOnly
								name='dropdown_archived_filter'
								onChange={value => setArchivedFilter(value.value)}
								selectedValue={dropdownData.find(d => d.value === archivedFilter)}
								imageJsx={<BadgePercent size={20} />}
								searchable={false}
							/>
						</>}
						<FlexDiv gap='6px'>
							<ViewPicker
								type='promotions'
								noBorder
								selectedView={selectedView}
								views={views.promotions}
								onViewChange={view => {
									setSelectedView(view);
									applyView(view);
								}}
								onDelete={deleteLocalView}
								onCreate={() => { setSelectedView(undefined); setIsViewOpen(true); }}
								onEdit={() => setIsViewOpen(true)}
								setEditedView={setEditedView}
							/>
							<ToolbarFilterButton
								onClick={() => {
									setIsOpenSummary(true);
									setFilterOpen(true);
								}}
								onDeleteFilter={() => {
									setFilterResult(defaultFilters);
								}}
								activeFilters={filterTreeLength(filterResult?.formatted)}
							/>
							{(!equal(filterResult?.formatted, selectedView?.data.filters.formatted ?? defaultFilters.formatted)
								|| (selectedView?.data.columns
									? !equal(orderedColumnIds, selectedView.data.columns.map(c => c.column))
									|| !equal(hiddenColumns, selectedView.data.columns.filter(c => c.hidden).map(c => c.column))
									: hiddenColumns.length > 0 || columnOrder.length > 0)
								|| inlineProducts !== (selectedView?.data.inlineProducts ?? false)
							) && <SidebarButton onClick={() => setIsViewOpen(true)}><Translate id='save_view' /></SidebarButton>}
						</FlexDiv>
						{visu === 'table' && <FlexDiv gap='6px'>
							<Switch value={inlineProducts} onChange={setInlineProducts} />
							<SwitchText><Translate id='campaigns.one_line_per_product' /></SwitchText>
						</FlexDiv>}
						{visu === 'table' && selectedRows.length > 0 && (inlineProducts
							? canUpdate && <SidebarButton onClick={() => setIsSelectionDeleteOpen(true)}>
								{translateToString('campaigns.remove_selected_products')} ({selectedRows.length})
							</SidebarButton>
							: isAllowedTo({ objectAction: 'DeleteCampaign' }) && <SidebarButton onClick={() => setIsSelectionDeleteOpen(true)}>
								{translateToString('delete')} ({selectedPromotionIds.length})
							</SidebarButton>)}
					</FlexDiv>
				</Restricted>
			</ToolbarBox>,
			bottomRightToolbarComponent: <ToolbarBox>
				<Restricted to={{ objectAction: 'ReadCampaign' }}>
					{visu === 'table' && <ColumnOrganizerWrapper
						categories={['campagnes', 'fields', 'products.title']}
						columns={organizerColumns}
						onSort={setColumnOrder}
						setHiddenColumns={setHiddenColumns}
						longIcon
					/>}
					<PageSwitch<'table' | 'timeline'> background={true} selectedValue={visu} list={[{ value: 'table', img: <Menu />, onClick: setVisu }, { value: 'timeline', img: <Calendar />, onClick: setVisu }]} />
					<Restricted to={{ objectAction: 'CreateCampaign' }}>
						<>
							<Add size='22px' onClick={() => setModalState({ isOpen: true })} />
							<Dropdown
								datalist={dataList}
								name='dropdown_actions'
								readOnly
								JSXButton={() => (
									<ToolbarImageContainer hasPointer><Ellipsis size={18} strokeWidth={2.5} color={GreySidely3} /></ToolbarImageContainer>
								)}
								dropdownStyle={{
									optionWidth: '200px',
									optionLeft: '-175px'
								}}
								onChange={async value => {
									if (value.value === 'import') {
										importPromotions((importCall) => {
											if (!importCall) return;
											setLoadingState(LoadingStateEnum.LOADING);
											importCall().then(refresh).catch(e => {
												console.error(e);
												setLoadingState(LoadingStateEnum.ERROR);
											});
										}, products, alert, fields);
									} else if (value.value === 'export') {
										exportPromotions(filteredPromotions, products, alert, fields);
									} else if (value.value === 'deleteAll') {
										setIsBulkDeleteOpen(true);
									}
								}}
								searchable={false}
							/>
						</>
					</Restricted>
				</Restricted>
			</ToolbarBox>
		});
	}, [visu, archivedFilter, products, filterResult, promotionFields, filteredPromotions, selectedView, inlineProducts, organizerColumns, hiddenColumns, columnOrder, orderedColumnIds, selectedRows.length, selectedPromotionIds.length, canUpdate]);

	React.useEffect(() => {
		refresh();
	}, []);


	const filteredPromotionsTimeLine = React.useMemo(() =>
		promotions.filter(p => (p.is_active || p.is_active === archivedFilter) && advancedFilterPromotions(p, filterResult, linkedFilterContext)),
	[archivedFilter, promotions, filterResult, linkedFilterContext]);
	const onSort = React.useCallback(res => setSort(res[0]), []);

	return <Restricted to={{ objectAction: 'ReadCampaign' }} fallback={<Unhautorized />}>
		{visu === 'timeline' && <GanttChart
			promotions={filteredPromotionsTimeLine}
			onEventClick={data => setModalState({ isOpen: true, data })}
			onNewEvent={() => setModalState({ isOpen: true })}
			height='calc(100vh - 118px)'
		/>}
		{visu === 'table' && <>
			<Table
				onClickFilter={id => {
					setSelectedFilter(id as FilterId);
					setIsOpenSummary(false);
					setFilterOpen(true);
				}}
				height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
				columns={columns}
				data={pageRows}
				onSort={onSort}
				initialSortBy={sort}
				hiddenColumns={hiddenColumns}
				sortColumns={orderedColumnIds}
				EnableResize
				onResize={value => Object.keys(value).length > 0 && setTableResize(value)}
				resizeValue={tableResize}
				selectable
				allSelectable
				isIdSelected={row => {
					const { selectedLines, pageSelectableRows } = selectionRef.current;
					if (row) return selectedLines.has(lineKey(row));
					return pageSelectableRows.length > 0 && pageSelectableRows.every(r => selectedLines.has(lineKey(r)));
				}}
				setSelectedCheckbox={row => {
					// the checkbox displays the returned state: report what happened
					const { selectedLines, pageSelectableRows, inlineProducts } = selectionRef.current;
					const next = new Set(selectedLines);
					let checked: boolean;
					if (row) {
						// an inlined line without a product has nothing to remove
						if (inlineProducts && !row.product) return false;
						const key = lineKey(row);
						checked = !next.has(key);
						if (checked) next.add(key);
						else next.delete(key);
					} else if (pageSelectableRows.length > 0 && pageSelectableRows.every(r => next.has(lineKey(r)))) {
						pageSelectableRows.forEach(r => next.delete(lineKey(r)));
						checked = false;
					} else {
						pageSelectableRows.forEach(r => next.add(lineKey(r)));
						checked = pageSelectableRows.length > 0;
					}
					setSelectedLines(next);
					return checked;
				}}
			/>
			<Pagination
				label={inlineProducts ? `${translateToString('campagnes')} (${translateToString('campaigns.with_products')})` : translateToString('campagnes')}
				amount={rows.length}
				currentCount={pageRows.length}
				steps={[25, 50, 100]}
				onChange={setPagination}
			/>
			<DurationPickerModalWrapper isOpen={rangePickerState.isOpen} isTop={rangePickerState.isTop} top={rangePickerState.top} left={rangePickerState.left} zIndex='10'>
				{rangePickerState.isOpen &&
					<RangePickerModal
						noAbsolute
						future
						onClickOut={() => setRangePickerState({ isOpen: false })}
						saveButton
						noDayButton
						start={rangePickerState.dates?.startDate ?? undefined}
						end={rangePickerState.dates?.endDate ?? undefined}
						onDateChange={dates => {
							setRangePickerState({ isOpen: false });
							rangePickerState.onDatesChange?.(dates);
						}}
						defaultFocus={rangePickerState.focus}
					/>}
			</DurationPickerModalWrapper>
		</>}
		<ComponentLoader loadingState={loadingState} allScreen noBackground />
		<PopupPromotion
			mode={modalState.data?.mode}
			isOpen={modalState.isOpen}
			setIsOpen={(isOpen: boolean) => setModalState({ isOpen })}
			promotion={modalState.data?.promotion}
			onCreate={body => postPromotion(body).then(refresh)}
			onUpdate={(id, body) => putPromotion(id, body).then(refresh)}
			onDelete={refresh}
		/>
		<PopupView
			isOpen={isViewOpen}
			view={editedView ?? selectedView}
			onClickOut={() => { setIsViewOpen(false); setEditedView(undefined); }}
			onValidation={(v) => { saveView(v).then(_ => setIsViewOpen(false)); setEditedView(undefined); }}
		/>
		<PopupDeletion
			records={1}
			translationKey='view'
			noVerification
			isOpen={deletionModalState.isOpen}
			onClickOut={() => setDeletionModalState({ isOpen: false })}
			onValidation={() => {
				if (deletionModalState.data) {
					deleteView(deletionModalState.data.id).then(_ => getViews().then(setViews));
				}
			}}
		/>
		<AdvancedFilters
			isOpen={filterOpen}
			setOpen={setFilterOpen}
			filterList={[
				{ category: translateToString('report_editor.crm_fields.Campaign'), filters: promotionFilters },
				{ category: translateToString('period'), filters: periodFilters },
				{ category: translateToString('products.title'), filters: productFilters },
				...companyFilterParams,
			]}
			onChange={values => {
				setFilterResult(values);
			}}
			filterValues={filterResult?.values}
			isOpenSummary={isOpenSummary}
			selectedFilter={selectedFilter}
		/>
		<PopupDeletion
			isOpen={isBulkDeleteOpen}
			onClickOut={() => setIsBulkDeleteOpen(false)}
			records={filteredPromotions.length}
			onValidation={() => {
				setLoadingState(LoadingStateEnum.LOADING);
				deletePromotionsById(filteredPromotions.map(p => p.id)).catch(() => setLoadingState(LoadingStateEnum.LOADED)).then(refresh);
			}}
			translationKey='campagnes'
		/>
		{/* the checked lines: promotions in normal mode, product removals when
		    inlining — the typed count validates exactly what the lines show */}
		<PopupDeletion
			isOpen={isSelectionDeleteOpen}
			onClickOut={() => setIsSelectionDeleteOpen(false)}
			records={inlineProducts ? selectedRows.length : selectedPromotionIds.length}
			translationKey='campagnes'
			customTitle={inlineProducts ? `${translateToString('campaigns.remove_selected_products')} (${selectedRows.length})` : undefined}
			customMessage={inlineProducts
				? translateToString('campaigns.remove_products_message', [['NUMBER', selectedRows.length.toString()], ['CAMPAIGNS', selectedPromotionIds.length.toString()]])
				: undefined}
			onValidation={() => {
				setLoadingState(LoadingStateEnum.LOADING);
				if (inlineProducts) {
					const byPromotion = new Map<number, number[]>();
					selectedRows.forEach(r => r.product && byPromotion.set(r.id, [...(byPromotion.get(r.id) ?? []), r.product.id]));
					Promise.all([...byPromotion].map(([promotionId, products_del]) => putPromotion(promotionId, { products_del })))
						.then(() => setSelectedLines(new Set()))
						.catch(() => setLoadingState(LoadingStateEnum.LOADED))
						.then(refresh);
				} else {
					deletePromotionsById(selectedPromotionIds)
						.then(() => setSelectedLines(new Set()))
						.catch(() => setLoadingState(LoadingStateEnum.LOADED))
						.then(refresh);
				}
			}}
		/>
	</Restricted>;
}
